import React from 'react';
import { errorMessage } from '../../api';
import type { FeasibilityCell, FeasibilityResult } from '../../types';
import { Badge, InlineAlert, cx } from '../../ui';
import type { FeasibilityState } from './useFeasibility';

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;

/** "based on 25% response and 10% drop-out", from what the server says it used. */
export const describeAssumptions = (result: FeasibilityResult) => {
  const a = result.assumptions;
  if (!a) return '';
  const parts = [`${pct(a.responseRate)} response`, `${pct(a.dropOutRate)} drop-out`];
  if (a.interviewLengthFactor !== 1) parts.push(`an interview length factor of ${a.interviewLengthFactor}`);
  return `based on ${parts.join(' and ')}`;
};

const people = (n: number | null | undefined) => (typeof n === 'number' ? n.toLocaleString() : '—');

/**
 * One quota row's feasibility, from the server's per-cell count: the matching panelists with a green tick when there
 * are enough for the row's wanted completes, a red cross when not. A dash means "not enough data" (never a zero).
 * The tooltip explains the numbers, including each gender when the row has a gender split.
 */
export function CellFeasibility({ cell, result }: { cell: FeasibilityCell | undefined; result: FeasibilityResult | null }) {
  if (!cell || cell.available === null || cell.feasible === null) {
    const why = cell && cell.notEnoughData && cell.notEnoughData.length ? cell.notEnoughData.join('; ') : 'Not enough data to count the panel for this row';
    return <span className="text-muted" title={why} aria-label={`Not enough data. ${why}`}>—</span>;
  }
  const lines = [
    `${people(cell.available)} matching panelists, about ${people(cell.feasibleCompletes)} feasible completes for ${people(cell.wanted)} wanted${result ? ` (${describeAssumptions(result)})` : ''}.`,
  ];
  (['male', 'female'] as const).forEach((g) => {
    const part = cell.genders && cell.genders[g];
    if (part) lines.push(`${g === 'male' ? 'Male' : 'Female'}: ${people(part.available)} panelists for ${people(part.wanted)} wanted${part.feasible === null ? '' : part.feasible ? ' (enough)' : ' (not enough)'}.`);
  });
  const ok = cell.feasible;
  return (
    <span className={cx('inline-flex items-center gap-1 font-semibold tabular-nums', ok ? 'text-ok' : 'text-danger')} title={lines.join(' ')} aria-label={`${ok ? 'Feasible' : 'Not feasible'}. ${lines.join(' ')}`}>
      {people(cell.available)}
      <span aria-hidden="true">{ok ? '✓' : '✗'}</span>
    </span>
  );
}

/**
 * The feasibility block of a summary card: the target-group figure, the panel behind it, and what it is based on.
 * It is a planning estimate from the panel as it is today, and it says so.
 */
export function FeasibilitySummary({ feasibility }: { feasibility: FeasibilityState }) {
  const { data, loading, error, updating } = feasibility;
  return (
    <div className="mt-4 border-t border-line pt-4" aria-label="Feasibility" role="region">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">Feasibility</h3>
        {data && data.feasible !== null && data.feasible !== undefined && <Badge tone={data.feasible ? 'ok' : 'danger'}>{data.feasible ? 'Feasible' : 'Not feasible'}</Badge>}
      </div>
      {error ? (
        <div className="mt-2"><InlineAlert tone="warn">Could not check feasibility: {errorMessage(error)}</InlineAlert></div>
      ) : !data ? (
        <p className="mt-2 text-sm text-muted">{loading || updating ? 'Checking the panel…' : 'Choose a country in Basics to see feasibility.'}</p>
      ) : data.status === 'notEnoughData' || data.feasibleCompletes === null ? (
        <div className="mt-2">
          <InlineAlert tone="info">
            <b>Not enough data to estimate yet.</b>
            {data.notEnoughData.length > 0 && <ul className="ml-4 mt-1 list-disc">{data.notEnoughData.map((r) => <li key={r}>{r}</li>)}</ul>}
          </InlineAlert>
        </div>
      ) : (
        <div className={cx('mt-2', updating && 'opacity-60')}>
          <p className="flex items-baseline justify-between">
            <span className={cx('text-xl font-extrabold tabular-nums', data.feasible ? 'text-ok' : 'text-danger')}>{people(data.feasibleCompletes)}</span>
            <span className="text-sm text-muted">feasible completes, of {people(data.wantedCompletes)} wanted</span>
          </p>
          <p className="mt-1 text-xs text-muted">
            {people(data.panelSize)} matching panelists, {describeAssumptions(data)}.
            {data.dataCoverage && data.dataCoverage.panelistsInCountry > 0 && (
              <> {people(data.dataCoverage.withDateOfBirth)} of {people(data.dataCoverage.panelistsInCountry)} panelists in this country have a date of birth and {people(data.dataCoverage.withGender)} a gender.</>
            )}
            {' '}Planning estimate, from the panel as it is today.
          </p>
        </div>
      )}
    </div>
  );
}
